<template>
  <div class="ai-image-tool">
    <section class="ai-hero">
      <div>
        <h2 class="ai-title"><i class="el-icon-magic-stick"></i> AI 图片生成</h2>
        <p class="ai-desc">输入提示词，调用 ChatGPT / OpenAI 生图能力生成图片，支持直接预览、重新生成和下载。</p>
      </div>
      <div class="ai-hero-badges">
        <span class="ai-badge">文生图</span>
        <span class="ai-badge">单张输出</span>
        <span class="ai-badge">可下载</span>
      </div>
    </section>

    <div class="ai-layout">
      <section class="ai-panel ai-form-panel">
        <div class="ai-panel-head">
          <div>
            <h3>生成参数</h3>
            <p>建议把主体、环境、光线、风格和画面比例写清楚，生成效果会更稳定。</p>
          </div>
          <el-tag :type="configured ? 'success' : 'warning'" size="small">
            {{ configured ? '服务已配置' : '服务未配置' }}
          </el-tag>
        </div>

        <el-alert
          v-if="configLoaded && !configured"
          type="warning"
          :closable="false"
          title="后端还没配置 AI 生图密钥，当前页面可以打开，但生成请求会失败。"
          show-icon
          class="ai-alert"
        />

        <div class="ai-policy-card">
          <div class="ai-policy-line">
            <span class="ai-policy-label">生成规则</span>
            <span class="ai-policy-value">{{ policySummary }}</span>
          </div>
          <div class="ai-policy-line">
            <span class="ai-policy-label">当前状态</span>
            <span class="ai-policy-value">{{ statusSummary }}</span>
          </div>
        </div>

        <el-form label-position="top" size="small" @submit.native.prevent>
          <el-form-item label="图片描述">
            <el-input
              v-model="form.prompt"
              type="textarea"
              :rows="8"
              resize="none"
              maxlength="1200"
              show-word-limit
              placeholder="例如：一只戴圆框眼镜的橘猫，坐在老上海风格咖啡馆窗边看报纸，晨光穿过百叶窗，写实摄影风格，细节丰富"
            />
          </el-form-item>

          <div class="ai-prompt-presets">
            <button
              v-for="preset in promptPresets"
              :key="preset.title"
              type="button"
              class="ai-preset"
              @click="applyPromptPreset(preset.text)"
            >
              <span class="ai-preset-title">{{ preset.title }}</span>
              <span class="ai-preset-text">{{ preset.text }}</span>
            </button>
          </div>

          <div class="ai-form-grid">
            <el-form-item label="尺寸">
              <el-select v-model="form.size" placeholder="请选择尺寸">
                <el-option
                  v-for="size in supportedSizes"
                  :key="size"
                  :label="sizeLabel(size)"
                  :value="size"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="输出格式">
              <el-select v-model="form.outputFormat" placeholder="请选择格式">
                <el-option
                  v-for="format in supportedFormats"
                  :key="format"
                  :label="formatLabel(format)"
                  :value="format"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="质量">
              <el-select v-model="form.quality" clearable placeholder="默认即可">
                <el-option label="默认" value="" />
                <el-option label="低" value="low" />
                <el-option label="中" value="medium" />
                <el-option label="高" value="high" />
              </el-select>
            </el-form-item>

            <el-form-item label="背景">
              <el-select v-model="form.background" clearable placeholder="默认即可">
                <el-option label="默认" value="" />
                <el-option label="自动" value="auto" />
                <el-option label="透明" value="transparent" />
                <el-option label="不透明" value="opaque" />
              </el-select>
            </el-form-item>
          </div>

          <div class="ai-size-hints">
            <p class="ai-size-tip">默认使用自动画幅，无需额外设置输出宽度和高度。</p>
          </div>

          <div class="ai-context-card">
            <div class="ai-context-card__head">
              <div>
                <strong>连续创作</strong>
                <p>{{ contextStatusText }}</p>
              </div>
              <el-switch
                v-model="form.useContext"
                :disabled="generating"
                active-text="开启"
                inactive-text="关闭"
              />
            </div>
          </div>

          <div class="ai-form-actions">
            <el-button type="primary" :loading="generating" @click="generateImage">
              {{ generating ? '正在生成...' : '生成图片' }}
            </el-button>
            <el-button @click="clearForm">清空描述</el-button>
            <el-button v-if="lastPrompt" @click="restoreLastPrompt">恢复上次描述</el-button>
          </div>
        </el-form>
      </section>

      <section class="ai-panel ai-result-panel">
        <div class="ai-panel-head">
          <div>
            <h3>生成结果</h3>
            <p>成功后会在这里显示图片，你可以直接下载或点击历史记录切换查看。</p>
          </div>
          <span v-if="activeResult && activeResult.sizeBytes" class="ai-meta-pill">
            {{ humanFileSize(activeResult.sizeBytes) }}
          </span>
        </div>

        <div v-if="generatedImageUrl" class="ai-result-wrap">
          <div v-if="form.useContext && canUseContext" class="ai-context-banner">
            <i class="el-icon-connection"></i>
            <span>下一次生成会基于当前选中的结果继续创作</span>
          </div>

          <div class="ai-result-preview" :class="{ 'is-generating': generating }">
            <img :src="generatedImageUrl" :alt="activeResult.prompt || 'AI 生成图片'" />
            <div v-if="generating" class="ai-loading-mask">
              <div class="ai-loading-copy">
                <i class="el-icon-loading"></i>
                <p>正在生成图片</p>
                <span>生成完成后会自动刷新到这里</span>
              </div>
            </div>
          </div>

          <div class="ai-result-actions">
            <el-button type="primary" icon="el-icon-download" @click="downloadActiveImage">下载图片</el-button>
            <el-button icon="el-icon-view" @click="openActiveImage">新窗口打开</el-button>
            <el-button :loading="generating" @click="generateImage">重新生成</el-button>
          </div>

          <div class="ai-result-meta">
            <div class="ai-result-line">
              <span class="ai-result-label">描述</span>
              <span class="ai-result-value">{{ activeResult.prompt }}</span>
            </div>
            <div class="ai-result-line">
              <span class="ai-result-label">参数</span>
              <span class="ai-result-value">
                {{ resultSizeLabel(activeResult) }} / {{ formatLabel(activeResult.outputFormat) }}
                <template v-if="activeResult.quality"> / 质量 {{ activeResult.quality }}</template>
                <template v-if="activeResult.background"> / 背景 {{ backgroundLabel(activeResult.background) }}</template>
              </span>
            </div>
            <div v-if="activeResult.usedContext" class="ai-result-line">
              <span class="ai-result-label">上下文</span>
              <span class="ai-result-value">本次已基于上一张结果继续创作</span>
            </div>
          </div>
        </div>

        <div v-else-if="generating" class="ai-loading-state">
          <div class="ai-loading-copy">
            <i class="el-icon-loading"></i>
            <p>正在生成图片</p>
            <span>结果区会在生成成功后自动显示图片</span>
          </div>
        </div>

        <div v-else class="ai-empty">
          <i class="el-icon-picture-outline"></i>
          <p>还没有生成图片。</p>
          <span>输入描述后点击“生成图片”，结果会显示在这里。</span>
        </div>

        <div v-if="history.length" class="ai-history">
          <div class="ai-history-head">
            <span>最近生成</span>
            <el-button type="text" @click="clearHistory">清空记录</el-button>
          </div>
          <div class="ai-history-grid">
            <button
              v-for="item in history"
              :key="item.filename"
              type="button"
              class="ai-history-item"
              :class="{ active: activeResult && activeResult.filename === item.filename }"
              @click="setActiveResult(item)"
            >
              <img :src="fullUrl(item.imageUrl)" :alt="item.prompt" />
              <span>{{ item.prompt }}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '../config'

export default {
  name: 'AiImageGenerator',
  data() {
    return {
      loadingConfig: false,
      configLoaded: false,
      configured: false,
      generating: false,
      pricing: {
        loginRequired: true,
        dailyFreeLimit: 2,
        coinCost: 2
      },
      userStatus: {
        loggedIn: false,
        todayGeneratedCount: 0,
        todayFreeRemaining: 2,
        currentGenerateCost: 0,
        nextGenerateCost: 0
      },
      supportedSizes: [ 'auto', '1024x1024', '1536x1024', '1024x1536' ],
      supportedFormats: [ 'png', 'jpeg', 'webp' ],
      customSizeLimits: {
        min: 64,
        max: 4096
      },
      form: {
        prompt: '',
        size: 'auto',
        outputFormat: 'png',
        quality: '',
        background: '',
        useContext: true
      },
      activeResult: null,
      history: [],
      lastPrompt: '',
      promptPresets: [
        {
          title: '产品海报',
          text: '一张高级感电商海报，主体是一瓶透明玻璃护肤精华，摆放在浅米色石材台面上，柔和棚拍光线，背景干净，商业摄影风格，细节清晰'
        },
        {
          title: '国风插画',
          text: '一幅细腻的国风插画，春日江南小镇，小桥流水，少女撑着油纸伞走过青石板路，薄雾和暖色晨光，画面唯美'
        },
        {
          title: '三维卡通',
          text: '一个可爱的三维卡通小机器人，圆润造型，蓝白配色，站在极简工作台上，柔和环境光，干净背景，高清渲染'
        }
      ]
    }
  },
  computed: {
    coinChargeEnabled() {
      return this.$store.state.coinChargeEnabled !== false
    },
    currentUserToken() {
      const user = this.$store.state.user
      return user && user.token ? String(user.token) : ''
    },
    currentCoins() {
      const user = this.$store.state.user
      if (user && user.coins != null) return Number(user.coins) || 0
      return Number(this.userStatus.coins) || 0
    },
    generatedImageUrl() {
      if (!this.activeResult || !this.activeResult.imageUrl) return ''
      return this.fullUrl(this.activeResult.imageUrl)
    },
    activeDownloadUrl() {
      if (!this.activeResult) return ''
      if (this.activeResult.downloadUrl) return this.fullUrl(this.activeResult.downloadUrl)
      if (this.activeResult.filename) {
        return this.fullUrl('/ai-image/download/' + encodeURIComponent(this.activeResult.filename))
      }
      return ''
    },
    canUseContext() {
      return !!(
        this.activeResult &&
        (this.activeResult.filename || this.activeResult.responseId || this.activeResult.prompt)
      )
    },
    contextStatusText() {
      if (!this.canUseContext) {
        return '先生成一张图片，或从下方历史中选中一张结果后，才可以基于它继续创作。'
      }
      if (!this.form.useContext) {
        return '当前已关闭连续创作，本次会按全新图片生成。'
      }
      return '当前已开启连续创作，会把选中的结果当作上下文继续生成。'
    },
    policySummary() {
      if (!this.coinChargeEnabled) {
        return '需登录后才可生成；当前为限时免费模式，生成图片不扣金币。'
      }
      const dailyFreeLimit = Number(this.pricing.dailyFreeLimit) || 2
      const coinCost = Number(this.pricing.coinCost) || 2
      return `需登录后才可生成；每个账号每天前 ${dailyFreeLimit} 张免费，超出后每张 ${coinCost} 金币。`
    },
    statusSummary() {
      if (!this.currentUserToken) {
        return '当前未登录，点击“生成图片”会先打开登录窗口。'
      }
      if (!this.coinChargeEnabled) {
        const generated = Number(this.userStatus.todayGeneratedCount) || 0
        return `今日已生成 ${generated} 张；当前为限时免费模式，本次不扣金币。`
      }
      const generated = Number(this.userStatus.todayGeneratedCount) || 0
      const freeRemaining = Math.max(0, Number(this.userStatus.todayFreeRemaining) || 0)
      const currentGenerateCost = Math.max(0, Number(this.userStatus.currentGenerateCost) || 0)
      if (currentGenerateCost > 0) {
        return `今日已生成 ${generated} 张，免费次数已用完；当前金币 ${this.currentCoins}，本次将扣 ${currentGenerateCost} 金币。`
      }
      return `今日已生成 ${generated} 张，还可免费生成 ${freeRemaining} 张；当前金币 ${this.currentCoins}，本次免费。`
    }
  },
  mounted() {
    this.loadConfig()
    this.$nextTick(() => {
      this.decorateFormFields()
    })
  },
  updated() {
    this.$nextTick(() => {
      this.decorateFormFields()
    })
  },
  watch: {
    currentUserToken() {
      this.loadConfig()
    }
  },
  methods: {
    decorateFormFields() {
      if (!this.$el) return

      const fieldNames = [
        'ai-image-prompt',
        'ai-image-size',
        'ai-image-output-format',
        'ai-image-quality',
        'ai-image-background'
      ]
      const controls = this.$el.querySelectorAll('.ai-form-panel textarea, .ai-form-panel .el-input__inner')
      fieldNames.forEach((fieldName, index) => {
        const control = controls[index]
        if (!control) return
        if (!control.id) control.id = fieldName
        if (!control.name) control.name = fieldName
        control.setAttribute('aria-label', fieldName)
      })
    },
    normalizeDimensionInput(value) {
      const normalized = parseInt(String(value == null ? '' : value).trim(), 10)
      if (!Number.isFinite(normalized) || normalized <= 0) return 0
      return normalized
    },
    resultSizeLabel(result) {
      if (!result) return ''
      const width = this.normalizeDimensionInput(result.outputWidth)
      const height = this.normalizeDimensionInput(result.outputHeight)
      const generationSize = String(result.generationSize || '')
      const sizeText = width && height ? `${width}x${height}` : this.sizeLabel(result.size)
      if (width && height && generationSize && generationSize !== `${width}x${height}`) {
        return `${sizeText}（生成画幅 ${this.sizeLabel(generationSize)}）`
      }
      return sizeText
    },
    applyUserStatus(status) {
      if (!status || typeof status !== 'object') return
      this.userStatus = Object.assign({}, this.userStatus, status)
    },
    applyConfigResponse(data) {
      const payload = data || {}
      this.configLoaded = true
      this.configured = !!payload.configured
      if (Array.isArray(payload.supportedSizes) && payload.supportedSizes.length) {
        this.supportedSizes = payload.supportedSizes
      }
      if (Array.isArray(payload.supportedFormats) && payload.supportedFormats.length) {
        this.supportedFormats = payload.supportedFormats
      }
      if (payload.customSizeLimits && typeof payload.customSizeLimits === 'object') {
        this.customSizeLimits = Object.assign({}, this.customSizeLimits, payload.customSizeLimits)
      }
      const defaults = payload.defaults || {}
      const defaultSize = this.supportedSizes.includes('auto')
        ? 'auto'
        : (defaults.size || this.supportedSizes[0] || this.form.size)
      this.form.size = defaultSize
      if (defaults.outputFormat) this.form.outputFormat = defaults.outputFormat
      if (payload.pricing && typeof payload.pricing === 'object') {
        this.pricing = Object.assign({}, this.pricing, payload.pricing)
      }
      if (payload.userStatus) {
        this.applyUserStatus(payload.userStatus)
      } else if (!this.currentUserToken) {
        this.applyUserStatus({
          loggedIn: false,
          todayGeneratedCount: 0,
          todayFreeRemaining: Number.isFinite(Number(this.pricing.dailyFreeLimit)) ? Number(this.pricing.dailyFreeLimit) : 2,
          currentGenerateCost: 0,
          nextGenerateCost: 0
        })
      }
    },
    requireLogin() {
      this.$message.warning('请先登录后再生成图片')
      this.$store.commit('SetShowLoginDialog', { show: true, tab: 'login' })
    },
    handleInsufficientCoins(message) {
      this.$message.warning(message || '金币不足，请先购买金币后再生成')
      this.$store.commit('SetOpenPayDialog', true)
    },
    loadConfig() {
      this.loadingConfig = true
      return this.$axios.get('/ai-image/config')
        .then((res) => {
          this.applyConfigResponse(res.data || {})
        })
        .catch(() => {
          this.configLoaded = true
          this.configured = false
        })
        .finally(() => {
          this.loadingConfig = false
        })
    },
    fullUrl(path) {
      if (!path) return ''
      if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) return path
      const baseUrl =
        (this.$axios && this.$axios.defaults && this.$axios.defaults.baseURL) ||
        API_BASE_URL
      return String(baseUrl || '').replace(/\/$/, '') + path
    },
    sizeLabel(size) {
      if (size === 'auto') return '自动'
      return size
    },
    formatLabel(format) {
      const value = String(format || '').toLowerCase()
      if (value === 'jpeg' || value === 'jpg') return 'JPG'
      if (value === 'webp') return 'WEBP'
      return 'PNG'
    },
    backgroundLabel(background) {
      const value = String(background || '').toLowerCase()
      if (value === 'transparent') return '透明'
      if (value === 'opaque') return '不透明'
      if (value === 'auto') return '自动'
      return value || '默认'
    },
    applyPromptPreset(text) {
      this.form.prompt = text
    },
    clearForm() {
      this.form.prompt = ''
    },
    restoreLastPrompt() {
      if (this.lastPrompt) this.form.prompt = this.lastPrompt
    },
    setActiveResult(item) {
      this.activeResult = item
    },
    pushHistory(item) {
      const next = [ item ].concat(this.history.filter(entry => entry.filename !== item.filename))
      this.history = next.slice(0, 8)
    },
    clearHistory() {
      this.history = []
      this.activeResult = null
    },
    generateImage() {
      const prompt = String(this.form.prompt || '').trim()
      if (!prompt) {
        this.$message.warning('请输入图片描述')
        return
      }

      if (!this.currentUserToken) {
        this.requireLogin()
        return
      }

      this.generating = true
      this.lastPrompt = prompt
      const payload = {
        prompt,
        size: this.form.size,
        outputFormat: this.form.outputFormat
      }
      if (this.form.quality) payload.quality = this.form.quality
      if (this.form.background) payload.background = this.form.background
      if (this.form.useContext && this.canUseContext) {
        payload.useContext = true
        if (this.activeResult.prompt) payload.previousPrompt = this.activeResult.prompt
        if (this.activeResult.responseId) payload.previousResponseId = this.activeResult.responseId
        if (this.activeResult.filename) payload.contextImageFilename = this.activeResult.filename
      }

      return this.$axios.post('/ai-image/generate', payload)
        .then((res) => {
          const response = res.data || {}
          if (response.coins != null) this.$store.commit('SetUserCoins', response.coins)
          if (response.pricing) this.pricing = Object.assign({}, this.pricing, response.pricing)
          if (response.userStatus) this.applyUserStatus(response.userStatus)
          const data = response.data || null
          if (!data || !data.imageUrl) {
            throw new Error('服务返回成功，但没有拿到图片地址')
          }

          const result = {
            ...data,
            prompt,
            generatedAt: Date.now()
          }
          this.activeResult = result
          this.pushHistory(result)
          this.$message.success('图片生成成功')
        })
        .catch((err) => {
          const response = err && err.response ? err.response : null
          const data = response && response.data ? response.data : null
          if (data && data.coins != null) this.$store.commit('SetUserCoins', data.coins)
          if (data && data.pricing) this.pricing = Object.assign({}, this.pricing, data.pricing)
          if (data && data.userStatus) this.applyUserStatus(data.userStatus)
          const message =
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
              ? String(err.response.data.message)
              : (err && err.message ? String(err.message) : '图片生成失败')
          const finalMessage = data && data.message ? String(data.message) : message
          if (data && data.code === 'LOGIN_REQUIRED') {
            this.requireLogin()
            return
          }
          if (data && data.code === 'INSUFFICIENT_COINS') {
            this.handleInsufficientCoins(finalMessage)
            return
          }
          this.$message.error(finalMessage)
        })
        .finally(() => {
          this.generating = false
        })
    },
    downloadActiveImage() {
      if (!this.activeDownloadUrl) return
      const link = document.createElement('a')
      link.href = this.activeDownloadUrl
      link.download = this.activeResult && this.activeResult.filename ? this.activeResult.filename : 'ai-image.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    openActiveImage() {
      if (!this.generatedImageUrl) return
      window.open(this.generatedImageUrl, '_blank', 'noopener')
    },
    humanFileSize(sizeBytes) {
      const size = Number(sizeBytes)
      if (!Number.isFinite(size) || size <= 0) return ''
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(2)} MB`
    }
  }
}
</script>

<style lang="less" scoped>
.ai-image-tool {
  min-height: 100%;
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(255, 215, 130, 0.26), transparent 30%),
    radial-gradient(circle at top right, rgba(129, 212, 250, 0.2), transparent 26%),
    linear-gradient(180deg, #fffdf8 0%, #f7fafc 100%);
}

.ai-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px;
  border-radius: 20px;
  background: linear-gradient(135deg, #fff7e6 0%, #ffffff 55%, #edf7ff 100%);
  box-shadow: 0 18px 40px rgba(34, 60, 80, 0.08);
  border: 1px solid rgba(239, 213, 171, 0.7);
}

.ai-title {
  margin: 0;
  font-size: 30px;
  line-height: 1.2;
  color: #243447;
}

.ai-desc {
  margin: 10px 0 0;
  max-width: 760px;
  font-size: 14px;
  line-height: 1.8;
  color: #5c6b7a;
}

.ai-hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ai-badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  color: #7d4b00;
  border: 1px solid rgba(255, 192, 108, 0.45);
  font-size: 12px;
  font-weight: 600;
}

.ai-layout {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 18px;
  margin-top: 18px;
}

.ai-panel {
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(220, 228, 236, 0.9);
  box-shadow: 0 16px 36px rgba(24, 39, 75, 0.08);
  padding: 22px;
}

.ai-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.ai-panel-head h3 {
  margin: 0;
  font-size: 18px;
  color: #243447;
}

.ai-panel-head p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #6d7b88;
  line-height: 1.7;
}

.ai-alert {
  margin-bottom: 16px;
}

.ai-policy-card {
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, #fffaf0 0%, #f8fbff 100%);
  border: 1px solid #e8edf3;
}

.ai-policy-line {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
}

.ai-policy-line + .ai-policy-line {
  margin-top: 8px;
}

.ai-policy-label {
  font-size: 12px;
  font-weight: 700;
  color: #7e8b96;
}

.ai-policy-value {
  font-size: 13px;
  line-height: 1.7;
  color: #33414f;
}

.ai-prompt-presets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.ai-preset {
  text-align: left;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #e5edf5;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.ai-preset:hover {
  transform: translateY(-2px);
  border-color: rgba(64, 158, 255, 0.45);
  box-shadow: 0 10px 22px rgba(64, 158, 255, 0.12);
}

.ai-preset-title {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #1f2d3d;
}

.ai-preset-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: #6d7b88;
}

.ai-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 14px;
}

.ai-size-hints {
  margin-top: 2px;
  margin-bottom: 12px;
}

.ai-size-tip {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #7a8896;
}

.ai-size-tip + .ai-size-tip {
  margin-top: 4px;
}

.ai-context-card {
  margin-bottom: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, #f9fbff 0%, #fffaf2 100%);
  border: 1px solid #e6edf5;
}

.ai-context-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.ai-context-card__head strong {
  display: block;
  font-size: 14px;
  color: #243447;
}

.ai-context-card__head p {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #6d7b88;
}

.ai-form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
}

.ai-result-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-context-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f3f8ff;
  border: 1px solid #dce9f8;
  color: #2f6fae;
  font-size: 13px;
  font-weight: 600;
}

.ai-context-banner i {
  font-size: 14px;
}

.ai-result-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 420px;
  border-radius: 18px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 244, 221, 0.86), rgba(237, 247, 255, 0.92)),
    repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.58) 0,
      rgba(255, 255, 255, 0.58) 12px,
      rgba(255, 255, 255, 0.26) 12px,
      rgba(255, 255, 255, 0.26) 24px
    );
  border: 1px solid rgba(218, 230, 242, 0.9);
}

.ai-result-preview.is-generating img {
  opacity: 0.34;
  filter: blur(2px);
  transform: scale(1.01);
}

.ai-result-preview img {
  max-width: 100%;
  max-height: 70vh;
  display: block;
  object-fit: contain;
  transition: opacity 0.22s ease, filter 0.22s ease, transform 0.22s ease;
}

.ai-loading-mask,
.ai-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.ai-loading-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 252, 245, 0.78) 0%, rgba(243, 248, 255, 0.84) 100%);
}

.ai-loading-state {
  min-height: 420px;
  border-radius: 18px;
  border: 1px dashed #d9e3ec;
  background: linear-gradient(180deg, #fffaf1 0%, #f7fbff 100%);
}

.ai-loading-copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 24px;
  color: #51606f;
}

.ai-loading-copy i {
  font-size: 40px;
  color: #409eff;
}

.ai-loading-copy p {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.ai-loading-copy span {
  font-size: 13px;
  line-height: 1.7;
  color: #6d7b88;
}

.ai-result-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ai-result-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fbff;
  border: 1px solid #e7eef5;
}

.ai-result-line {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 12px;
}

.ai-result-label {
  font-size: 12px;
  font-weight: 700;
  color: #7e8b96;
}

.ai-result-value {
  font-size: 13px;
  line-height: 1.7;
  color: #33414f;
  word-break: break-word;
}

.ai-meta-pill {
  padding: 7px 10px;
  border-radius: 999px;
  background: #eff7ff;
  color: #2f6fae;
  font-size: 12px;
  font-weight: 600;
}

.ai-empty {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-radius: 18px;
  border: 1px dashed #d9e3ec;
  background: linear-gradient(180deg, #fbfdff 0%, #f6f9fc 100%);
  color: #7e8b96;
  text-align: center;
  padding: 24px;
}

.ai-empty i {
  font-size: 54px;
  margin-bottom: 14px;
  color: #aab8c5;
}

.ai-empty p {
  margin: 0;
  font-size: 18px;
  color: #51606f;
}

.ai-empty span {
  margin-top: 8px;
  max-width: 320px;
  font-size: 13px;
  line-height: 1.7;
}

.ai-history {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #edf2f7;
}

.ai-history-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  color: #33414f;
  font-weight: 700;
}

.ai-history-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.ai-history-item {
  padding: 8px;
  border-radius: 14px;
  border: 1px solid #e3ebf2;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.ai-history-item:hover,
.ai-history-item.active {
  transform: translateY(-2px);
  border-color: rgba(64, 158, 255, 0.45);
  box-shadow: 0 10px 22px rgba(64, 158, 255, 0.12);
}

.ai-history-item img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 10px;
  display: block;
}

.ai-history-item span {
  display: -webkit-box;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #51606f;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

@media (max-width: 1024px) {
  .ai-layout {
    grid-template-columns: 1fr;
  }

  .ai-prompt-presets {
    grid-template-columns: 1fr;
  }

  .ai-history-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .ai-image-tool {
    padding: 12px;
  }

  .ai-hero,
  .ai-panel {
    padding: 16px;
    border-radius: 16px;
  }

  .ai-hero {
    flex-direction: column;
  }

  .ai-title {
    font-size: 24px;
  }

  .ai-form-grid {
    grid-template-columns: 1fr;
  }

  .ai-result-preview,
  .ai-empty {
    min-height: 300px;
  }

  .ai-history-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ai-result-line {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>

<style lang="less">
.dark-mode .ai-image-tool {
  background:
    radial-gradient(circle at top left, rgba(255, 180, 80, 0.12), transparent 30%),
    radial-gradient(circle at top right, rgba(90, 170, 255, 0.12), transparent 24%),
    linear-gradient(180deg, var(--bg-page) 0%, var(--bg-page) 100%);
}

.dark-mode .ai-hero,
.dark-mode .ai-panel,
.dark-mode .ai-policy-card,
.dark-mode .ai-context-card,
.dark-mode .ai-context-banner,
.dark-mode .ai-preset,
.dark-mode .ai-history-item {
  background: var(--bg-card) !important;
  border-color: var(--border-color) !important;
}

.dark-mode .ai-title,
.dark-mode .ai-panel-head h3,
.dark-mode .ai-context-card__head strong,
.dark-mode .ai-result-value,
.dark-mode .ai-history-head {
  color: var(--text-primary) !important;
}

.dark-mode .ai-desc,
.dark-mode .ai-panel-head p,
.dark-mode .ai-policy-label,
.dark-mode .ai-policy-value,
.dark-mode .ai-context-card__head p,
.dark-mode .ai-context-banner,
.dark-mode .ai-preset-text,
.dark-mode .ai-empty,
.dark-mode .ai-history-item span {
  color: var(--text-secondary) !important;
}

.dark-mode .ai-result-meta,
.dark-mode .ai-meta-pill,
.dark-mode .ai-result-preview,
.dark-mode .ai-loading-state,
.dark-mode .ai-empty {
  background: rgba(255, 255, 255, 0.03) !important;
  border-color: var(--border-color) !important;
}

.dark-mode .ai-loading-mask {
  background: rgba(15, 23, 42, 0.62) !important;
}

.dark-mode .ai-loading-copy p,
.dark-mode .ai-loading-copy span,
.dark-mode .ai-loading-copy i {
  color: var(--text-primary) !important;
}
</style>
